"use client";

import { clsx } from "clsx";
import { AnimatePresence, MotionConfig } from "motion/react";
import * as m from "motion/react-m";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import { flushSync } from "react-dom";
import { useLocale, useTranslations } from "next-intl";

import type { ICmsAssetOrbTexture } from "@mw/shared/payload-types";
import { EventName } from "@mw/shared/analyticsEventNames";

import { captureEvent } from "@/app/_analytics/analytics";

import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconCallCancelFilled from "@ui/central-icons/icons/IconCallCancelFilled";
import IconCallFilled from "@ui/central-icons/icons/IconCallFilled";
import { useAgentSession } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/useAgentSession";
import { buildVoiceStartOptions } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/SpeechEngine/speechEngineDeveloperDemoHelpers";
import { getLocalStorage, LocalStorageKeys, setLocalStorage } from "@utilities/localStorage";
import { Orb } from "@ui/components/core/Orb/Orb";
import { SwitcherButton, type SwitcherOption } from "@ui/components/core/SwitcherButton";
import { TEXTURES, type OrbTextureName } from "@ui/components/core/Orb/textures";
import { ConvAITerms } from "@ui/components/demos/ConvAI/ConvAITermsModal";
import { formatTime } from "@ui/lib/time";
import SpeechEngineEnableButton from "./SpeechEngineEnableButton";
import { SpeechEngineHelpChatDemo } from "./SpeechEngineHelpChatDemo";
import { resolveSpeechEnginePreviewScenario } from "./resolveSpeechEnginePreviewScenario";
import { buildScriptedSpeechEngineHandoff } from "./speechEngineAgentHandoff";
import {
  isSpeechEngineHandoffActive,
  resolveSpeechEngineHandoffMode,
  type SpeechEngineHandoffMode,
} from "./speechEngineHandoffMode";

import type { CmsChatScenario } from "@ui/components/core/ChatScenario/cms";
import type { AgentSessionStartOptions } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/useAgentSession";

const SPEECH_ENGINE_DEMO_AGENT_ID = "agent_8901krx4cy7bevjtngk78rcr9j44";
const DEFAULT_SPEECH_ENGINE_ORB_TEXTURE = "speech-engine" satisfies OrbTextureName;

function resolveSpeechEngineOrbTexture(value: ICmsAssetOrbTexture | null | undefined): OrbTextureName {
  if (value && value in TEXTURES) {
    return value;
  }
  return DEFAULT_SPEECH_ENGINE_ORB_TEXTURE;
}

type SpeechEngineOrbWithCallHandle = {
  acceptConsentAndStartVoiceSession: () => void;
};

const SpeechEngineOrbWithCall = forwardRef(function SpeechEngineOrbWithCall(
  {
    agentId,
    voiceId,
    previewHandoffTranscript,
    previewFirstMessage,
    handoffMode,
    agentHandoffInstruction,
    orbTexture,
    termsModalBlocking,
    onRequestTermsConsent,
    onCloseTerms,
    onAgreeTerms,
    orbLayoutId,
  }: {
    agentId: string;
    voiceId?: string;
    handoffMode: SpeechEngineHandoffMode;
    previewHandoffTranscript: string | null;
    previewFirstMessage: string | null;
    agentHandoffInstruction?: string | null;
    orbTexture: OrbTextureName;
    termsModalBlocking: boolean;
    onRequestTermsConsent: () => void;
    onCloseTerms: () => void;
    onAgreeTerms: () => void;
    orbLayoutId?: string;
  },
  ref: Ref<SpeechEngineOrbWithCallHandle>,
) {
  const t = useTranslations("ClientKeys.ConvAI");
  const tSpeech = useTranslations("ClientKeys.speech_engine_demo");
  const locale = useLocale();
  const voiceHintId = "speech-engine-voice-hint";
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const session = useAgentSession({
    agentId,
    voiceId,
    defaultMode: "voice",
  });

  const buildVoiceSessionStartOptions = useCallback((): AgentSessionStartOptions => {
    return buildVoiceStartOptions({
      locale,
      handoffMode,
      previewHandoff:
        previewHandoffTranscript || previewFirstMessage
          ? { transcript: previewHandoffTranscript, firstMessage: previewFirstMessage }
          : null,
      handoffInstruction: agentHandoffInstruction,
    });
  }, [locale, handoffMode, previewHandoffTranscript, previewFirstMessage, agentHandoffInstruction]);

  const voiceCallActive = session.sessionIsActive && session.mode === "voice";
  const isVoiceCallConnected = voiceCallActive && session.status === "connected";

  const endSessionRef = useRef(session.endSession);
  endSessionRef.current = session.endSession;

  useEffect(() => {
    return () => {
      endSessionRef.current();
    };
  }, []);

  useEffect(() => {
    if (!isVoiceCallConnected) {
      setElapsedSecs(0);
      return;
    }
    setElapsedSecs(0);
    const intervalId = setInterval(() => {
      setElapsedSecs(s => s + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isVoiceCallConnected]);

  useImperativeHandle(
    ref,
    () => ({
      acceptConsentAndStartVoiceSession() {
        setLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS, "true");
        session.startVoiceSession(buildVoiceSessionStartOptions());
      },
    }),
    [session, buildVoiceSessionStartOptions],
  );

  function startVoiceWithTermsCheck() {
    const termsAccepted = getLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS);
    if (termsAccepted) {
      session.startVoiceSession(buildVoiceSessionStartOptions());
    } else {
      onRequestTermsConsent();
    }
  }

  function handleCallToggle() {
    if (session.sessionIsActive) {
      session.endSession();
    } else if (session.status === "disconnected") {
      startVoiceWithTermsCheck();
    }
  }

  const callButtonShadow =
    "0 0 1px 0 rgba(0, 0, 0, 0.40), 0 1px 1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px 0 rgba(0, 0, 0, 0.04)";

  const callButtonLabel = voiceCallActive
    ? session.status === "connecting"
      ? tSpeech("connecting_call_aria")
      : t("end_call")
    : t("start_call");

  const orbBlock = (
    <div className='tw-relative tw-mx-auto tw-w-[12.5rem] md:tw-w-[17.5rem]'>
      <m.div
        layoutId={orbLayoutId}
        className='tw-relative tw-w-full tw-aspect-square tw-rounded-full tw-overflow-hidden'
        // Subtle 1.03 active-state scale when a voice call is live. Sibling call-pill is unaffected
        // (it's positioned absolute over the same outer container, not nested inside this m.div).
        animate={{ scale: voiceCallActive ? 1.03 : 1 }}
        transition={{
          layout: { type: "spring", stiffness: 200, damping: 30 },
          scale: { type: "spring", stiffness: 220, damping: 22 },
        }}
      >
        <Orb texture={orbTexture} exposure={0.13} conversation={session.conversation} />
        <div className='tw-absolute tw-inset-0 tw-rounded-full tw-ring-0.5 tw-ring-inset tw-ring-black/7.5' />
        <div className='tw-absolute tw-inset-0 tw-rounded-full tw-ring-1 tw-ring-inset tw-ring-white/50' />
      </m.div>
      <m.button
        type='button'
        className='tw-group tw-absolute tw-inset-0 tw-flex tw-size-full tw-items-center tw-justify-center tw-outline-none'
        onClick={handleCallToggle}
        aria-label={callButtonLabel}
        aria-describedby={!voiceCallActive ? voiceHintId : undefined}
        aria-busy={voiceCallActive && session.status === "connecting"}
        // Fade in after the orb's layoutId spring (~0.4s) settles, so the call pill doesn't sit
        // in its final centered position while the orb is still scaling up around it.
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.45, ease: "easeOut" }}
      >
        <div
          className='tw-flex tw-size-[3.75rem] tw-items-center tw-justify-center tw-rounded-full tw-bg-black tw-text-white group-focus-visible:tw-outline-focus'
          style={{ boxShadow: callButtonShadow }}
        >
          {voiceCallActive ? (
            <IconCallCancelFilled className={clsx(iconStrokeFix, "tw-size-5")} aria-hidden />
          ) : (
            <IconCallFilled className={clsx(iconStrokeFix, "tw-size-5")} aria-hidden />
          )}
        </div>
      </m.button>
    </div>
  );

  return (
    <div className='tw-relative tw-flex tw-h-full tw-max-h-[40rem] tw-min-h-[28rem] tw-w-full tw-max-w-md tw-flex-col tw-items-center tw-pt-9'>
      <m.div
        inert={termsModalBlocking}
        className='tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-items-center'
        animate={{
          scale: termsModalBlocking ? 0.95 : 1,
          filter: termsModalBlocking ? "blur(8px)" : "blur(0px)",
        }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className='tw-min-h-0 tw-w-full tw-flex-1' aria-hidden />
        <div className='tw-flex tw-shrink-0 tw-flex-col tw-items-center'>{orbBlock}</div>
        <div className='tw-flex tw-min-h-0 tw-w-full tw-flex-[1.5] tw-flex-col'>
          <div className='tw-grid tw-min-h-0 tw-flex-1 tw-w-full tw-grid-rows-[5fr_auto_2fr] tw-items-center tw-justify-items-center'>
            <div className='tw-min-h-0' aria-hidden />
            {/* min-h-11 (44px) reserves space for two lines of tw-type-sm text (22px line-height) so the
                orb above doesn't shift when toggling between the two-line hint and the single-line timer. */}
            <div className='tw-flex tw-min-h-11 tw-items-center tw-justify-center'>
              {voiceCallActive ? (
                <m.time
                  key='elapsed'
                  className='tw-type-sm tw-tabular-nums tw-text-center tw-text-white'
                  dateTime={`PT${elapsedSecs}S`}
                  aria-label={tSpeech("call_elapsed_a11y", { time: formatTime(elapsedSecs) })}
                  aria-live='polite'
                  initial={{ opacity: 0, filter: "blur(8px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  {formatTime(elapsedSecs)}
                </m.time>
              ) : (
                <m.p
                  key='hint'
                  id={voiceHintId}
                  className='tw-max-w-sm tw-text-balance tw-px-2 tw-text-center tw-type-sm tw-text-white'
                  initial={{ opacity: 0, filter: "blur(8px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  transition={{ duration: 0.3, delay: 0.55, ease: "easeOut" }}
                >
                  {tSpeech("talk_to_agent_hint")}
                </m.p>
              )}
            </div>
            <div className='tw-min-h-0' aria-hidden />
          </div>
        </div>
      </m.div>

      <AnimatePresence>
        {termsModalBlocking && (
          <m.div
            key='terms-popover'
            role='dialog'
            aria-label={tSpeech("terms_overlay_aria")}
            className='tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-p-4'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <m.div
              // Buttons sit at 12px from card edges (px-3 + pb-3); content (intro + bullets) gets an
              // extra 12px via `contentClassName` so it lands at 24px from card edges on sides/top.
              className='tw-pointer-events-auto tw-w-full tw-max-w-[20rem] tw-rounded-3xl tw-bg-white tw-px-3 tw-pb-3 tw-pt-6 tw-shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_0_rgba(0,0,0,0.04),0_2px_4px_0_rgba(0,0,0,0.04)]'
              initial={{ opacity: 0, scale: 0.95, filter: "blur(8px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 0.95, filter: "blur(8px)" }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <ConvAITerms onAgree={onAgreeTerms} onClose={onCloseTerms} compact contentClassName='tw-px-3' />
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
});

SpeechEngineOrbWithCall.displayName = "SpeechEngineOrbWithCall";

export type SpeechEngineLayoutProps = {
  agentId?: string;
  voiceId?: string;
  /** Orb visual (`agents-*` / `creative-*`) from CMS; defaults to Agents 1. */
  orbTexture?: ICmsAssetOrbTexture | null;
  chatScenario?: CmsChatScenario | null;
  previewReplayPauseMs?: number | null;
  previewMessageDelayMs?: number | null;
  previewPartDelayMs?: number | null;
  /**
   * Show this many scripted-preview messages instantly in their final state when the demo mounts; the animated
   * reveal starts from the next message. Defaults to 0 (animate from the first message).
   */
  previewInitialBubbleCount?: number | null;
  /**
   * Passed through to scripted preview chat (`ChatScenario` typing bubbles). Defaults true.
   */
  previewTypingIndicatorBetweenMessages?: boolean;
  /** Passed through to scripted preview chat — fades messages at the top. Defaults true. */
  previewTopFade?: boolean;
  /** Override the handoff instruction sent as a dynamic variable to the agent. Falls back to the default. */
  agentHandoffInstruction?: string | null;
  handoffMode?: SpeechEngineHandoffMode | null;
};

type PreviewHandoffData = {
  transcript: string | null;
  /** Opening voice line for the live session (`firstMessage` override). */
  firstMessage: string | null;
};

function capturePreviewHandoffData(
  chatScenario: CmsChatScenario | null | undefined,
  settledMessageCount: number,
  handoffMode: SpeechEngineHandoffMode,
): PreviewHandoffData {
  if (!isSpeechEngineHandoffActive(handoffMode)) {
    return { transcript: null, firstMessage: null };
  }

  const scenario = resolveSpeechEnginePreviewScenario(chatScenario ?? null);
  const initialMessageCount = scenario.initialMessages?.length ?? 0;
  const allMessages = [...(scenario.initialMessages ?? []), ...scenario.messages];
  // `settledMessageCount` is scoped to `scenario.messages` only; offset for always-visible initialMessages.
  const settledInAllMessages = initialMessageCount + settledMessageCount;
  const handoff = buildScriptedSpeechEngineHandoff({
    messages: allMessages,
    receivedMessageCount: settledInAllMessages,
  });

  return { transcript: handoff.transcript, firstMessage: handoff.firstMessage };
}

export default function SpeechEngineLayout({
  agentId = SPEECH_ENGINE_DEMO_AGENT_ID,
  voiceId,
  orbTexture: orbTextureFromCms,
  chatScenario,
  previewReplayPauseMs,
  previewMessageDelayMs,
  previewPartDelayMs,
  previewInitialBubbleCount,
  previewTypingIndicatorBetweenMessages = true,
  previewTopFade = true,
  agentHandoffInstruction,
  handoffMode: handoffModeFromCms,
}: SpeechEngineLayoutProps = {}) {
  const handoffMode = resolveSpeechEngineHandoffMode(handoffModeFromCms);
  const [isSpeechEngineEnabled, setIsSpeechEngineEnabled] = useState(false);
  const [speechTermsModalOpen, setSpeechTermsModalOpen] = useState(false);
  const [previewHandoff, setPreviewHandoff] = useState<PreviewHandoffData | null>(null);
  const speechOrbRef = useRef<SpeechEngineOrbWithCallHandle>(null);
  const initialBubbleCount =
    typeof previewInitialBubbleCount === "number" && previewInitialBubbleCount > 0
      ? Math.floor(previewInitialBubbleCount)
      : 0;
  // Static prefix bubbles are fully revealed on mount; animated ones only count once settled.
  const previewSettledMessageCountRef = useRef(initialBubbleCount);
  const tSpeech = useTranslations("ClientKeys.speech_engine_demo");
  const orbTexture = resolveSpeechEngineOrbTexture(orbTextureFromCms);

  useEffect(() => {
    if (!isSpeechEngineEnabled) {
      setSpeechTermsModalOpen(false);
    }
  }, [isSpeechEngineEnabled]);

  return (
    <MotionConfig reducedMotion='never'>
      <section
        aria-label={tSpeech("widget_region_a11y")}
        className='tw-relative tw-z-10 tw-flex tw-size-full tw-min-h-0 tw-flex-1 tw-items-center tw-justify-center'
      >
        <AnimatePresence initial={false}>
          {isSpeechEngineEnabled && (
            <m.div
              key='speech-engine-tabs'
              // Mobile: 12px from left/top/right edges; each tab fills 50% of the row.
              // md+: original centered pill at 40px from top (natural width per tab).
              // Pointer-events-none on the wrapper so the call-button area below stays interactive;
              // SwitcherButton's inner buttons re-enable their own pointer events.
              className={clsx(
                "tw-pointer-events-none tw-absolute tw-inset-x-3 tw-top-3 tw-flex md:tw-inset-x-0 md:tw-top-10 md:tw-justify-center",
                speechTermsModalOpen ? "tw-z-0" : "tw-z-[1]",
              )}
              initial={{ opacity: 0, y: 4, scale: 0.95, filter: "blur(8px)" }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                filter: "blur(0px)",
                transition: { duration: 0.25, delay: 0.55, ease: "easeOut" },
              }}
              exit={{
                opacity: 0,
                y: 4,
                scale: 0.95,
                filter: "blur(8px)",
                transition: { duration: 0.25, ease: "easeOut" },
              }}
            >
              <div className='tw-pointer-events-auto tw-w-full md:tw-w-auto'>
                <SwitcherButton
                  options={
                    [
                      { id: "chatbot", label: tSpeech("tab_your_chatbot") },
                      { id: "speech-engine", label: tSpeech("tab_speech_engine") },
                    ] satisfies SwitcherOption[]
                  }
                  selectedIndex={1}
                  onSelect={index => {
                    if (index !== 0) return;
                    captureEvent(EventName.mw_speech_engine_demo_back_clicked);
                    setPreviewHandoff(null);
                    setIsSpeechEngineEnabled(false);
                  }}
                  size='md'
                  surface='dark'
                  inactiveTextColor='white'
                  showRing={false}
                  textClass='tw-type-sm'
                  className='max-md:!tw-py-0 [&_button]:tw-flex-1 md:[&_button]:tw-flex-none max-md:[&_button]:!tw-px-3 max-md:[&_button]:tw-min-w-0 max-md:[&_button>*]:tw-truncate'
                />
              </div>
            </m.div>
          )}
        </AnimatePresence>
        <div className='tw-relative tw-z-0 tw-flex tw-h-full tw-w-full tw-shrink-0 tw-items-center tw-justify-center'>
          <AnimatePresence mode='popLayout' initial={true}>
            {isSpeechEngineEnabled ? (
              <m.div
                key='speech-engine-enabled'
                className='tw-flex tw-w-full tw-justify-center'
                // No scale on this wrapper — the orb's layoutId spring already does the visual scaling;
                // composing a parent transform would fight that animation.
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <SpeechEngineOrbWithCall
                  ref={speechOrbRef}
                  agentId={agentId}
                  voiceId={voiceId}
                  handoffMode={handoffMode}
                  previewHandoffTranscript={previewHandoff?.transcript ?? null}
                  previewFirstMessage={previewHandoff?.firstMessage ?? null}
                  agentHandoffInstruction={agentHandoffInstruction}
                  orbTexture={orbTexture}
                  orbLayoutId='speech-engine-orb'
                  termsModalBlocking={speechTermsModalOpen}
                  onRequestTermsConsent={() => setSpeechTermsModalOpen(true)}
                  onCloseTerms={() => setSpeechTermsModalOpen(false)}
                  onAgreeTerms={() => {
                    flushSync(() => {
                      setSpeechTermsModalOpen(false);
                    });
                    speechOrbRef.current?.acceptConsentAndStartVoiceSession();
                  }}
                />
              </m.div>
            ) : (
              <m.div
                key='speech-engine-preview'
                className='tw-flex tw-h-full tw-w-full tw-justify-center max-md:tw-px-9'
                initial={{ opacity: 0, scale: 0.95, filter: "blur(8px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0, scale: 0.95, filter: "blur(8px)" }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <SpeechEngineHelpChatDemo
                  loop
                  chatScenario={chatScenario ?? undefined}
                  onSettledMessageCountChange={count => {
                    previewSettledMessageCountRef.current = count;
                  }}
                  replayPauseMs={previewReplayPauseMs}
                  additionalMessageDelayMs={previewMessageDelayMs}
                  additionalPartDelayMs={previewPartDelayMs}
                  initialVisibleMessageCount={previewInitialBubbleCount}
                  typingIndicatorBetweenMessages={previewTypingIndicatorBetweenMessages}
                  topFade={previewTopFade}
                />
              </m.div>
            )}
          </AnimatePresence>
        </div>
        <AnimatePresence>
          {!isSpeechEngineEnabled && (
            <m.div
              key='enable-button-overlay'
              className='tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center max-md:tw-px-5'
              initial={{ opacity: 0, filter: "blur(8px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, filter: "blur(8px)" }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <SpeechEngineEnableButton
                className='tw-pointer-events-auto'
                orbTexture={orbTexture}
                orbLayoutId='speech-engine-orb'
                onPress={() => {
                  captureEvent(EventName.mw_speech_engine_demo_enable_clicked);
                  setPreviewHandoff(
                    capturePreviewHandoffData(chatScenario, previewSettledMessageCountRef.current, handoffMode),
                  );
                  setIsSpeechEngineEnabled(true);
                }}
              />
            </m.div>
          )}
        </AnimatePresence>
      </section>
    </MotionConfig>
  );
}
